// @soft-depth
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  ArrowLeftRight,
  Building2,
  Check,
  ChevronDown,
  Copy,
  FileSignature,
  History,
  LayoutList,
  Maximize2,
  Minus,
  Pencil,
  Plus,
  Receipt,
  Search,
  Share2,
  Type,
  X
} from 'lucide-react';
import { Browser } from '@capacitor/browser';
import { sanitize } from 'dompurify';
import _ from 'lodash';
import AccessCodeGate from '../../../../components/AccessCodeGate/AccessCodeGate';
import BottomSheetOrDialog from '../../../../components/BottomSheetOrDialog/BottomSheetOrDialog';
import LoadingBackdrop from '../../../../components/LoadingBackdrop/LoadingBackdrop';
import ShareButtonGroup from '../../../../components/ShareButtonGroup/ShareButtonGroup';
import { NumberFormatWrapper } from '../../../../components/NumberFormatTextField/NumberFormatTextField';
import SoftDepthScope from '../../../../design-system/SoftDepthScope';
import LegacyThemeScope from '../../../../design-system/LegacyThemeScope';
import { DISPLAY_VARIANTS } from '../../../../design-system/tokens';
import type { HueName } from '../../../../design-system/tokens';
import { LANGUAGE } from '../../../../constants/language';
import { LOADING } from '../../../../constants/phase';
import { pickCopy } from '../../../../utils/localeCopy';
import { usePrevious } from '../../../../utils/hooks';
import { copyToClipboard } from '../../../../utils/native/copy';
import NativeOrWeb from '../../../../utils/native';
import { getMostLikelyInsurer, isInsurerOther, isInsurerWhatIf } from '../../../../utils/insurer';
import { insurerRegionFilter } from '../../../../utils/region';
import EN_TRANSLATION from '../../../../store/locale/messages/en.json';
import ZH_TRANSLATION from '../../../../store/locale/messages/zh.json';
import ZH_HANT_HK_TRANSLATION from '../../../../store/locale/messages/zh-Hant-HK.json';
import ChequeVersion, { ChequeFace } from './ChequeVersion';
import './chequeOut.css';

/**
 * 支票精靈 — the adviser picks the payee, types the amount, and the tool writes
 * the amount out in words for the client to copy onto the cheque. The payee
 * name and the words are the page's output, so they sit in one cheque-shaped
 * card the adviser can step up to the size the client reads across the table.
 *
 * Soft Depth (business) for the whole page: a deliberate redesign, so colour
 * and text size come from theme keys only. The pieces still legacy inside it —
 * the share row and the access-code gate — keep the legacy theme.
 */

// numbo's language plugins attach themselves to the instance; the generated
// files carry no types, and they must load after numbo itself (they write onto
// window.numbo), which requires in this order at module scope keep.
type NumboApi = { convert: (value: string, ...options: string[]) => string; [plugin: string]: unknown };
const numboApi: NumboApi = require('../../../../utils/numbo');
numboApi.zhTW = require('../../../../utils/numbo/numbo.zhTW');
numboApi.zhCN = require('../../../../utils/numbo/numbo.zhCN');

const NUMBO_LANGUAGE: Record<string, string> = {
  [LANGUAGE.EN]: 'en',
  [LANGUAGE.ZH]: 'zhCN',
  [LANGUAGE.ZH_HANT_HK]: 'zhTW'
};

/** 預設收款人,與改版前相同。 */
const DEFAULT_INSURER_ID = '59b0f0a97af33f1d6074cbfd';
/** 「其他」收款人 —— 選它之後收款人一欄可以直接打字。 */
const OTHER_INSURER_ID = '5a1be0f9dfaa60227838e426';
/**
 * 不收保費支票的公司,不在收款人清單裡出現。Transamerica Life (Bermuda):官網 FAQ 只列
 * 電匯、本地轉帳和轉數快(2026-10-31 查核)。
 */
const NO_CHEQUE_INSURER_IDS = ['59b0f59f7af33f1d6074cc11'];

const ACCESS_CODES = ['oneteam', 'dynasty', 'pfplus', 'apollo'];
// CAM 的收款人是固定的一行字,不在 insurers 裡,只有用這幾個訪問碼進來才看得到。
const CAM_ACCESS_CODES = ['oneteam', 'dynasty', 'apollo'];
const CAM_PAYEE_NAME = 'Kerberos (Nominee) Limited - Client Account';
const CAM_INSURER: Insurer = {
  _id: 'convoy',
  title: { en: 'CAM', zh: 'CAM', 'zh-Hant-HK': 'CAM' },
  payeeName: { en: CAM_PAYEE_NAME, zh: CAM_PAYEE_NAME, 'zh-Hant-HK': CAM_PAYEE_NAME },
  image: 'img/insurer_hkconvoy.png'
};

// 收款人清單裡選中的一格用綠色(hue 是「哪一類東西」,不是狀態)。
const PAYEE_HUE: HueName = 'emerald';

// 語言切換每格同寬,滑動的白卡才對得準。
const LANGUAGE_SEGMENT_WIDTH = 64;

const CHEQUE_LANGUAGES = [
  { value: LANGUAGE.EN, label: 'ENG' },
  { value: LANGUAGE.ZH_HANT_HK, label: '繁中' },
  { value: LANGUAGE.ZH, label: '简中' }
];

const PAYEE_GRID = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))',
  gap: 1
};

// 只給螢幕閱讀器的文字(複製成功的提示)。
const SR_ONLY = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  m: '-1px',
  p: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap'
} as const;

// 顯示方式(標準／支票版)只是這部機的偏好。預設是支票版;只有自己選過標準才回到標準,
// 讀寫失敗也用支票版。
const VIEW_KEY = 'chequeWizard.view';
type ChequeView = 'classic' | 'cheque';

const readView = (): ChequeView => {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'classic' ? 'classic' : 'cheque';
  } catch (e) {
    return 'cheque';
  }
};

const writeView = (view: ChequeView) => {
  try {
    window.localStorage.setItem(VIEW_KEY, view);
  } catch (e) {
    // 私密瀏覽:不記也沒關係。
  }
};

const VIEW_SEGMENT_WIDTH = 92;

// 最近用過的收款人:只存在這部機的瀏覽器裡,是方便而不是資料,讀寫失敗就當沒有。
const RECENT_PAYEES_KEY = 'chequeWizard.recentPayees';
const RECENT_PAYEES_MAX = 3;

const readRecentPayees = (): string[] => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(RECENT_PAYEES_KEY) || '[]');
    return Array.isArray(stored) ? stored.filter(id => typeof id === 'string') : [];
  } catch (e) {
    return [];
  }
};

const writeRecentPayees = (ids: string[]) => {
  try {
    window.localStorage.setItem(RECENT_PAYEES_KEY, JSON.stringify(ids));
  } catch (e) {
    // 私密瀏覽或空間滿了:不記也沒關係。
  }
};

const COPY = {
  'zh-Hant-HK': {
    subtitle: '選收款人、填金額，大寫即時顯示',
    change: '更改',
    smaller: '縮小字體',
    bigger: '放大字體',
    wordsHint: '填入金額，這裡就會顯示支票上的大寫',
    pickPayee: '選擇收款人',
    searchPayee: '搜尋保險公司',
    noMatch: '找不到相符的保險公司',
    share: '分享給客戶',
    copyWords: '複製大寫',
    copied: '已複製',
    present: '給客戶看',
    closePresent: '關閉',
    recent: '最近使用',
    allPayees: '全部保險公司',
    view: '顯示方式',
    amountPlaceholder: '輸入金額',
    viewClassic: '標準',
    viewCheque: '支票版'
  },
  zh: {
    subtitle: '选收款人、填金额，大写即时显示',
    change: '更改',
    smaller: '缩小字体',
    bigger: '放大字体',
    wordsHint: '填入金额，这里就会显示支票上的大写',
    pickPayee: '选择收款人',
    searchPayee: '搜索保险公司',
    noMatch: '找不到相符的保险公司',
    share: '分享给客户',
    copyWords: '复制大写',
    copied: '已复制',
    present: '给客户看',
    closePresent: '关闭',
    recent: '最近使用',
    allPayees: '全部保险公司',
    view: '显示方式',
    amountPlaceholder: '输入金额',
    viewClassic: '标准',
    viewCheque: '支票版'
  },
  en: {
    subtitle: 'Pick the payee, type the amount, read the words',
    change: 'Change',
    smaller: 'Smaller text',
    bigger: 'Bigger text',
    wordsHint: 'Type an amount and the words for the cheque appear here',
    pickPayee: 'Choose the payee',
    searchPayee: 'Search insurers',
    noMatch: 'No insurer matches',
    share: 'Share with the client',
    copyWords: 'Copy the words',
    copied: 'Copied',
    present: 'Show the client',
    closePresent: 'Close',
    recent: 'Recently used',
    allPayees: 'All insurers',
    view: 'View',
    amountPlaceholder: 'Type amount',
    viewClassic: 'Standard',
    viewCheque: 'Cheque'
  }
};

type LocaleMap = Record<string, string>;

type Insurer = {
  _id: string;
  title: LocaleMap;
  payeeName?: LocaleMap;
  image?: string;
  exchangeRatePage?: LocaleMap;
};

type IntlLike = {
  formatMessage: (descriptor: { id: string }) => string;
  locale: string;
};

/** BottomSheetOrDialog is JavaScript behind injectIntl; named for the props passed here. */
type SurfaceProps = {
  open: boolean;
  onClose: () => void;
  header?: React.ReactNode;
  content?: React.ReactNode;
  DialogProps?: Record<string, unknown>;
  DialogParams?: Record<string, unknown>;
};
const Surface = BottomSheetOrDialog as unknown as React.ComponentType<SurfaceProps>;

// The Redux store, the intl messages and the insurer utils are untyped.
export interface ChequeOutViewProps {
  intl: IntlLike;
  location: { search?: string; state?: { insurer?: Insurer } };
  userDetails?: any;
  getPhase?: unknown;
  updatePhase?: unknown;
  chequeWizard?: { insurerId?: string; insurerName?: string };
  insurers?: Insurer[];
  renderHeaderButton: (value: boolean) => void;
  getChequeWizard: () => void;
  updateChequeWizard: (payload: { insurerId: string; insurerName: string }) => void;
  resetChequeWizard: () => void;
  selectedLocale: (language: string) => void;
}

const localised = (map: LocaleMap | undefined, locale: string): string => _.get(map, locale) || _.get(map, 'en') || '';

const ChequeOutView = (props: ChequeOutViewProps) => {
  const {
    intl,
    location,
    userDetails,
    getPhase,
    chequeWizard,
    updatePhase,
    insurers,
    renderHeaderButton,
    getChequeWizard,
    updateChequeWizard,
    resetChequeWizard,
    selectedLocale
  } = props;

  const [cashValue, setCashValue] = useState<number | string>('');
  const [cashValueOut, setCashValueOut] = useState('');
  const [chequeLanguage, setChequeLanguage] = useState<string>(LANGUAGE.EN);
  const [insurerModalOpen, setInsurerModalOpen] = useState(false);
  const [payeeQuery, setPayeeQuery] = useState('');
  const [selectedChequeInsurerId, setSelectedChequeInsurerId] = useState(DEFAULT_INSURER_ID);
  const [zoom, setZoom] = useState(1);
  const [chequeLanguageEdit, setChequeLanguageEdit] = useState(false);
  const [otherPayeeName, setOtherPayeeName] = useState('');
  // 訪問碼由 AccessCodeGate 收；allowed 為 true 時它直接放行，所以這裡一直是空
  // 字串，CAM 收款人也不會出現 —— 保留原狀，等關卡真的啟用時才生效。
  const [accessCode] = useState('');
  const [wordsCopied, setWordsCopied] = useState(false);
  const copiedTimer = useRef<number>();
  const [presenting, setPresenting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [view, setView] = useState<ChequeView>(readView);
  const [recentPayeeIds, setRecentPayeeIds] = useState<string[]>(readRecentPayees);

  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

  const t = (id: string) => intl.formatMessage({ id });
  const ui = pickCopy(COPY, intl.locale);
  const displayVariant = DISPLAY_VARIANTS[zoom];
  const languageIndex = CHEQUE_LANGUAGES.findIndex(option => option.value === chequeLanguage);
  const prevGetPhase = usePrevious(getPhase);

  useEffect(() => {
    const targetLanguage = location.search ? new URLSearchParams(location.search).get('l') || LANGUAGE.ZH : LANGUAGE.ZH;
    if (!userDetails?._id) {
      selectedLocale(targetLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    return () => {
      resetChequeWizard();
    };
  }, [resetChequeWizard]);

  useEffect(() => {
    renderHeaderButton(false);
  }, [renderHeaderButton]);

  useEffect(() => {
    if (userDetails && userDetails.language && !chequeLanguageEdit) {
      setChequeLanguage(userDetails.language);
    }
  }, [chequeLanguageEdit, userDetails]);

  // set selected insurer according location state
  useEffect(() => {
    const { insurer } = location.state || {};
    if (insurer) {
      setSelectedChequeInsurerId(insurer._id);
    } else if (userDetails && userDetails._id) {
      getChequeWizard();
    }
  }, [location, getChequeWizard, userDetails]);

  useEffect(() => {
    const { insurerId, insurerName } = chequeWizard || {};
    if (getPhase === true && prevGetPhase === LOADING) {
      // not other insurer
      if (insurerId !== OTHER_INSURER_ID) {
        const insurer = (insurers || []).find(one => one._id === insurerId);
        if (insurer) {
          setSelectedChequeInsurerId(insurer._id);
        }
      } else {
        setSelectedChequeInsurerId(insurerId);
        setOtherPayeeName(insurerName || '');
      }
    }
  }, [chequeWizard, getPhase, insurers, prevGetPhase]);

  useEffect(() => {
    const cashValueStr = (cashValue || '').toString();
    let nextCashValueOut;

    const numboLanguage = NUMBO_LANGUAGE[chequeLanguage];

    if (chequeLanguage === LANGUAGE.EN) {
      if (cashValueStr && cashValueStr.indexOf('.') === -1) {
        nextCashValueOut = numboApi.convert(cashValueStr, 'check').split('and No Cent Only')[0] + 'Only';
      } else {
        nextCashValueOut = cashValueStr ? numboApi.convert(cashValueStr, 'check') : '';
      }
    } else {
      nextCashValueOut = cashValueStr ? numboApi.convert(cashValueStr, numboLanguage, 'check') : '';
    }
    setCashValueOut(nextCashValueOut);
  }, [chequeLanguage, cashValue]);

  // 收款人清單：自己公司排最前，「其他」排最後，「如果」和不收支票的公司不列出。
  const payeeOptions = useMemo(() => {
    const options: Insurer[] = (insurers || []).filter(
      one => insurerRegionFilter(one) && !NO_CHEQUE_INSURER_IDS.includes(one._id)
    );

    const mostLikely = getMostLikelyInsurer(insurers, userDetails?.company);
    const mostLikelyIndex = mostLikely ? options.findIndex(one => one._id === mostLikely._id) : -1;
    if (mostLikelyIndex > -1) {
      options.unshift(options.splice(mostLikelyIndex, 1)[0]);
    }

    if (CAM_ACCESS_CODES.indexOf(accessCode) > -1) {
      options.push(CAM_INSURER);
    }

    const whatIfIndex = options.findIndex(isInsurerWhatIf);
    if (whatIfIndex > -1) {
      options.splice(whatIfIndex, 1);
    }

    const otherIndex = options.findIndex(isInsurerOther);
    if (otherIndex > -1) {
      options.push(options.splice(otherIndex, 1)[0]);
    }

    return options;
  }, [insurers, userDetails, accessCode]);

  const isOtherPayee = selectedChequeInsurerId === OTHER_INSURER_ID;
  // 存起來的收款人有可能被地區篩掉，找不到就回到未篩的清單再找一次。
  const selectedInsurer =
    payeeOptions.find(one => one._id === selectedChequeInsurerId) ||
    (insurers || []).find(one => one._id === selectedChequeInsurerId);
  const payeeName = isOtherPayee ? otherPayeeName : localised(selectedInsurer?.payeeName, chequeLanguage);
  const exchangeRatePage = localised(selectedInsurer?.exchangeRatePage, chequeLanguage);

  const isGuest = !userDetails?._id;
  const enableShare = location?.search ? new URLSearchParams(location.search).get('enable_share') === 'true' : false;
  const hideShareButtons = isGuest && !enableShare;

  const shownPayeeOptions = useMemo(() => {
    const query = payeeQuery.trim().toLowerCase();
    if (!query) return payeeOptions;
    // 每種語言的名稱都搜:顧問打「hsbc」時,畫面上顯示的可能是「滙豐」。
    return payeeOptions.filter(one =>
      [..._.values(one.title), ..._.values(one.payeeName)].join(' ').toLowerCase().includes(query)
    );
  }, [payeeOptions, payeeQuery]);

  // 最近用過的收款人排在清單頂部;搜尋時不顯示,免得同一間出現兩次。
  const recentPayeeOptions = useMemo(
    () => recentPayeeIds.map(id => payeeOptions.find(one => one._id === id)).filter((one): one is Insurer => !!one),
    [recentPayeeIds, payeeOptions]
  );
  const showRecentPayees = !payeeQuery.trim() && recentPayeeOptions.length > 0;

  const copyWords = async () => {
    const copied = await copyToClipboard(cashValueOut);
    if (!copied) return;
    setWordsCopied(true);
    window.clearTimeout(copiedTimer.current);
    copiedTimer.current = window.setTimeout(() => setWordsCopied(false), 1500);
  };

  // The rate page must not replace this one: on the app `window.open` navigates the
  // same webview (and the amount typed is lost), so the app opens the in-app
  // browser overlay instead, as every other external link here does.
  const openExchangeRatePage = () => {
    if (NativeOrWeb.isNativePlatform()) {
      Browser.open({ url: exchangeRatePage });
    } else {
      window.open(exchangeRatePage, '_blank', 'noopener,noreferrer');
    }
  };

  const handleChangeCashValue = (event: { target: { value?: number | string } }) => {
    setCashValue(event.target.value ?? '');
  };

  const handleInsurerModalOpen = () => {
    setPayeeQuery('');
    setInsurerModalOpen(true);
  };

  const handleInsurerModalClose = () => {
    setInsurerModalOpen(false);
  };

  const handleInsurerEdit = (insurer: Insurer) => {
    setSelectedChequeInsurerId(insurer._id);
    const nextRecent = [insurer._id, ...recentPayeeIds.filter(id => id !== insurer._id)].slice(0, RECENT_PAYEES_MAX);
    setRecentPayeeIds(nextRecent);
    writeRecentPayees(nextRecent);
    handleInsurerModalClose();
    if (userDetails && userDetails._id) {
      updateChequeWizard({
        insurerId: insurer._id,
        insurerName: insurer._id === OTHER_INSURER_ID ? otherPayeeName : ''
      });
    }
  };

  const updateOtherPayeeName = () => {
    if (!userDetails || !userDetails._id) {
      return;
    }
    updateChequeWizard({
      insurerId: selectedChequeInsurerId,
      insurerName: isOtherPayee ? otherPayeeName : ''
    });
  };

  // 分享給客戶的那段文字：收款人 + 金額 + 大寫，用支票本身的語言。
  const shareMessage = () => {
    let translation;
    switch (chequeLanguage) {
      case LANGUAGE.ZH_HANT_HK:
        translation = ZH_HANT_HK_TRANSLATION;
        break;
      case LANGUAGE.ZH:
        translation = ZH_TRANSLATION;
        break;
      default:
        translation = EN_TRANSLATION;
    }
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    const messages = translation.messages as Record<string, string>;
    const payeeLine = (payeeName || '').replace(/<br>/g, '\n');
    const formattedCashValue = formatter.format(Number(cashValue) || 0);
    return `*${messages['Payee']}:* \n${payeeLine}\n\n*${messages['Amount']}:* ${formattedCashValue} \n${
      cashValueOut || ''
    }`;
  };

  // 收款人清單裡的一格(最近使用和全部共用)。
  const renderPayeeOption = (option: Insurer) => {
    const selected = selectedChequeInsurerId === option._id;
    return (
      <Box
        key={option._id}
        component="button"
        type="button"
        aria-pressed={selected}
        data-testid="cheque-payee-option"
        onClick={() => handleInsurerEdit(option)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          p: 1.25,
          minHeight: 78,
          cursor: 'pointer',
          appearance: 'none',
          font: 'inherit',
          borderRadius: 1.5,
          border: '1px solid',
          // 選中的一格用收款人那一段的色系,和卡片上的圖示對得上。
          borderColor: selected ? `hue.${PAYEE_HUE}.main` : 'divider',
          bgcolor: selected ? `hue.${PAYEE_HUE}.soft` : 'background.paper',
          boxShadow: selected ? 'none' : 1,
          transition:
            'border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
          '&:hover': {
            borderColor: selected ? `hue.${PAYEE_HUE}.main` : `hue.${PAYEE_HUE}.border`,
            boxShadow: selected ? 'none' : 2
          },
          '&:active': { transform: 'scale(0.97)' },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2
          },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
        }}
      >
        {!isGuest && option.image && (
          <Box component="img" src={option.image} alt="" sx={{ height: 26, maxWidth: '100%', objectFit: 'contain' }} />
        )}
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {selected && (
            <Box component="span" sx={{ display: 'flex', color: `hue.${PAYEE_HUE}.dark` }}>
              <Check size={14} aria-hidden="true" />
            </Box>
          )}
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              textAlign: 'center'
            }}
          >
            {localised(option.title, intl.locale)}
          </Typography>
        </Stack>
      </Box>
    );
  };

  // 段落標題:和 Smart IDEA 卡片標題一樣,強調色的淡底小方塊 + 圖示。
  const sectionHead = (icon: React.ReactNode, label: string, action?: React.ReactNode) => (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1, minHeight: 28 }}>
      <Box
        component="span"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: 28,
          height: 28,
          borderRadius: 0.75,
          bgcolor: 'primary.soft',
          color: 'primary.main'
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" component="h2" sx={{ flex: 1, minWidth: 0, m: 0, color: 'text.primary' }}>
        {label}
      </Typography>
      {action}
    </Stack>
  );

  // Controls both views share, so the classic card and the 支票版 do the same things.
  const otherPayeeInput = (
    <OutlinedInput
      multiline
      minRows={2}
      name="otherPayeeName"
      value={otherPayeeName}
      onChange={event => setOtherPayeeName(event.target.value)}
      onBlur={updateOtherPayeeName}
      placeholder={t('Type payee name here')}
      inputProps={{ 'aria-label': t('Payee'), 'data-testid': 'cheque-payee-input' }}
      sx={{ typography: displayVariant, '& textarea': { textAlign: 'center' } }}
    />
  );

  const fxButton =
    exchangeRatePage && !isGuest ? (
      <Button
        variant="outlined"
        color="inherit"
        size="small"
        data-testid="cheque-fx"
        startIcon={<ArrowLeftRight size={14} aria-hidden="true" />}
        onClick={openExchangeRatePage}
        sx={{ py: 0.25 }}
      >
        {t('forexExchange')}
      </Button>
    ) : undefined;

  const amountInput = (
    <OutlinedInput
      name="cashValue"
      value={cashValue}
      onChange={handleChangeCashValue as any}
      placeholder="0"
      startAdornment={<InputAdornment position="start">$</InputAdornment>}
      inputComponent={NumberFormatWrapper as any}
      inputProps={{
        inputMode: 'decimal',
        pattern: '[0-9]*',
        'aria-label': t('Amount'),
        'data-testid': 'cheque-amount'
      }}
      sx={{ typography: 'h4', '& input': { py: 0.75, textAlign: 'right' } }}
    />
  );

  // The same two fields as they sit on the 支票版: borderless, written in pen ink,
  // the cheque's own box and line around them.
  const chequeAmountInput = (
    <InputBase
      name="cashValue"
      value={cashValue}
      onChange={handleChangeCashValue as any}
      placeholder={ui.amountPlaceholder}
      inputComponent={NumberFormatWrapper as any}
      inputProps={{
        inputMode: 'decimal',
        pattern: '[0-9]*',
        'aria-label': t('Amount'),
        'data-testid': 'cheque-amount'
      }}
      sx={{
        width: '100%',
        typography: 'h5',
        color: 'cheque.ink',
        '& input': { p: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
      }}
    />
  );

  const chequePayeeInput = (
    <InputBase
      multiline
      name="otherPayeeName"
      value={otherPayeeName}
      onChange={event => setOtherPayeeName(event.target.value)}
      onBlur={updateOtherPayeeName}
      placeholder={t('Type payee name here')}
      inputProps={{ 'aria-label': t('Payee'), 'data-testid': 'cheque-payee-input' }}
      sx={{ width: '100%', typography: 'h5', '& textarea': { p: 0, color: 'cheque.ink' } }}
    />
  );

  const copyWordsButton = (
    <>
      <IconButton
        aria-label={wordsCopied ? ui.copied : ui.copyWords}
        data-testid="cheque-words-copy"
        onClick={copyWords}
        sx={{
          width: 34,
          height: 34,
          flexShrink: 0,
          color: wordsCopied ? 'success.main' : 'text.secondary',
          '&:hover': { color: wordsCopied ? 'success.main' : 'primary.main' }
        }}
      >
        {wordsCopied ? <Check size={18} aria-hidden="true" /> : <Copy size={18} aria-hidden="true" />}
      </IconButton>
      <Box component="span" role="status" sx={SR_ONLY}>
        {wordsCopied ? ui.copied : ''}
      </Box>
    </>
  );

  const languageSwitch = (
    <Box
      role="group"
      sx={{
        position: 'relative',
        alignSelf: 'center',
        display: 'inline-flex',
        p: 0.5,
        bgcolor: 'grey.100',
        borderRadius: 1.5
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: 4,
          bottom: 4,
          left: 4,
          width: LANGUAGE_SEGMENT_WIDTH,
          borderRadius: 1,
          bgcolor: 'background.paper',
          boxShadow: 1,
          transform: `translateX(${Math.max(languageIndex, 0) * 100}%)`,
          opacity: languageIndex === -1 ? 0 : 1,
          transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
        }}
      />
      {CHEQUE_LANGUAGES.map(option => {
        const selected = chequeLanguage === option.value;
        return (
          <Box
            key={option.value}
            component="button"
            type="button"
            aria-pressed={selected}
            data-testid={`cheque-language-${option.value}`}
            onClick={() => {
              setChequeLanguage(option.value);
              setChequeLanguageEdit(true);
            }}
            sx={{
              position: 'relative',
              typography: 'subtitle2',
              width: LANGUAGE_SEGMENT_WIDTH,
              py: 0.5,
              cursor: 'pointer',
              appearance: 'none',
              border: 'none',
              borderRadius: 1,
              bgcolor: 'transparent',
              color: selected ? 'primary.dark' : 'text.secondary',
              transition: 'color 250ms ease',
              '&:hover': { color: 'text.primary' },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2
              },
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
            }}
          >
            {option.label}
          </Box>
        );
      })}
    </Box>
  );

  const presentButton = (
    <Button
      variant="contained"
      data-testid="cheque-present"
      startIcon={<Maximize2 size={16} aria-hidden="true" />}
      onClick={() => setPresenting(true)}
      sx={{ mt: 0.5 }}
    >
      {ui.present}
    </Button>
  );

  // 數字金額,寫在支票方格裡的樣子。
  const amountText = cashValue
    ? Number(cashValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '';

  const changeView = (next: ChequeView) => {
    setView(next);
    writeView(next);
  };

  // 標準／支票版切換:和語言切換同一種滑動白卡。
  const viewIndex = view === 'cheque' ? 0 : 1;
  const viewSwitch = (
    <Box
      role="group"
      aria-label={ui.view}
      sx={{
        position: 'relative',
        display: 'inline-flex',
        p: 0.5,
        bgcolor: 'grey.100',
        borderRadius: 1.5,
        flexShrink: 0
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: 4,
          bottom: 4,
          left: 4,
          width: VIEW_SEGMENT_WIDTH,
          borderRadius: 1,
          bgcolor: 'background.paper',
          boxShadow: 1,
          transform: `translateX(${viewIndex * 100}%)`,
          transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
        }}
      />
      {(
        [
          // 支票版 first: it is the default.
          { value: 'cheque', label: ui.viewCheque, icon: <FileSignature size={15} aria-hidden="true" /> },
          { value: 'classic', label: ui.viewClassic, icon: <LayoutList size={15} aria-hidden="true" /> }
        ] as const
      ).map(option => {
        const selected = view === option.value;
        return (
          <Box
            key={option.value}
            component="button"
            type="button"
            aria-pressed={selected}
            data-testid={`cheque-view-${option.value}`}
            onClick={() => changeView(option.value)}
            sx={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              typography: 'subtitle2',
              width: VIEW_SEGMENT_WIDTH,
              py: 0.5,
              cursor: 'pointer',
              appearance: 'none',
              border: 'none',
              borderRadius: 1,
              bgcolor: 'transparent',
              color: selected ? 'primary.dark' : 'text.secondary',
              transition: 'color 250ms ease',
              '&:hover': { color: 'text.primary' },
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
            }}
          >
            {option.icon}
            {option.label}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <div>
      {(getPhase === LOADING || updatePhase === LOADING) && <LoadingBackdrop />}
      <div id="wrapper">
        <div id="page-content-wrapper">
          <div className="cheque-out">
            <div className="nav-top-margin">
              <AccessCodeGate intl={intl} allowed={true} accessCodes={ACCESS_CODES}>
                <SoftDepthScope mode="business">
                  <Box data-testid="cheque-page" sx={{ px: { xs: 1.5, sm: 2 }, py: 2 }}>
                    <Stack sx={{ maxWidth: view === 'cheque' ? 760 : 560, mx: 'auto', gap: 1.5 }}>
                      {viewSwitch}
                      {view === 'classic' ? (
                        <>
                          <Stack direction="row" sx={{ alignItems: 'center', gap: 1, px: 0.5 }}>
                            {/* 頁名已在 app 標題列上,這裡只說怎麼用。 */}
                            <Typography
                              variant="body2"
                              component="p"
                              sx={{ flex: 1, minWidth: 0, m: 0, color: 'text.secondary' }}
                            >
                              {ui.subtitle}
                            </Typography>
                            {/* 字體大小：客戶在對面要看得見，放大縮小留在最順手的位置。 */}
                            <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                              <Button
                                variant="outlined"
                                color="inherit"
                                aria-label={ui.smaller}
                                data-testid="cheque-zoom-out"
                                disabled={zoom <= 0}
                                onClick={() => setZoom(Math.max(0, zoom - 1))}
                                sx={{ minWidth: 34, px: 0 }}
                              >
                                <Minus size={16} aria-hidden="true" />
                              </Button>
                              <Box component="span" sx={{ display: 'flex', color: 'text.secondary' }}>
                                <Type size={22} aria-hidden="true" />
                              </Box>
                              <Button
                                variant="outlined"
                                color="inherit"
                                aria-label={ui.bigger}
                                data-testid="cheque-zoom-in"
                                disabled={zoom >= DISPLAY_VARIANTS.length - 1}
                                onClick={() => setZoom(Math.min(DISPLAY_VARIANTS.length - 1, zoom + 1))}
                                sx={{ minWidth: 34, px: 0 }}
                              >
                                <Plus size={16} aria-hidden="true" />
                              </Button>
                            </Stack>
                          </Stack>

                          {/* 支票本身：收款人在上、金額在下，和手上那張支票同一個次序。 */}
                          <Paper component="section" sx={{ p: 2 }}>
                            <Stack sx={{ gap: 1.25 }}>
                              {sectionHead(
                                <Building2 size={16} aria-hidden="true" />,
                                t('Payee'),
                                <Button
                                  variant="outlined"
                                  color="inherit"
                                  size="small"
                                  data-testid="cheque-payee-edit"
                                  startIcon={<Pencil size={14} aria-hidden="true" />}
                                  onClick={handleInsurerModalOpen}
                                  sx={{ py: 0.25 }}
                                >
                                  {ui.change}
                                </Button>
                              )}
                              {isOtherPayee ? (
                                otherPayeeInput
                              ) : (
                                <Typography
                                  variant={displayVariant}
                                  component="p"
                                  data-testid="cheque-payee-name"
                                  sx={{ m: 0, textAlign: 'center', wordBreak: 'break-word' }}
                                  // 保險公司的收款人名稱帶 <br>，照原樣斷行。
                                  dangerouslySetInnerHTML={{ __html: sanitize(payeeName) }}
                                />
                              )}

                              <Divider sx={{ borderStyle: 'dashed', my: 0.5 }} />

                              {sectionHead(<Receipt size={16} aria-hidden="true" />, t('Amount'), fxButton)}
                              {amountInput}

                              {/* 大寫金額：這一頁真正要給客戶看的一行。 */}
                              {cashValueOut ? (
                                <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
                                  {/* 左邊留同寬的空位,大寫才會真的置中。 */}
                                  <Box sx={{ width: 34, flexShrink: 0 }} />
                                  <Typography
                                    variant={displayVariant}
                                    component="p"
                                    data-testid="cheque-words"
                                    sx={{
                                      flex: 1,
                                      minWidth: 0,
                                      m: 0,
                                      py: 0.5,
                                      textAlign: 'center',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {cashValueOut}
                                  </Typography>
                                  {copyWordsButton}
                                </Stack>
                              ) : (
                                <Typography
                                  variant="body2"
                                  component="p"
                                  sx={{ m: 0, py: 0.5, color: 'text.secondary', textAlign: 'center' }}
                                >
                                  {ui.wordsHint}
                                </Typography>
                              )}

                              {languageSwitch}

                              {presentButton}
                            </Stack>
                          </Paper>
                        </>
                      ) : (
                        <ChequeVersion
                          locale={intl.locale}
                          payeeHtml={payeeName}
                          amountText={amountText}
                          words={cashValueOut}
                          onPayeeClick={handleInsurerModalOpen}
                          payeeField={isOtherPayee ? chequePayeeInput : undefined}
                          amountField={chequeAmountInput}
                          wordsAction={copyWordsButton}
                        >
                          {/* The cheque is the form; only what is not on a cheque stays here. */}
                          {languageSwitch}
                          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
                          {fxButton}
                          {presentButton}
                        </ChequeVersion>
                      )}

                      <Paper component="section" sx={{ p: 2 }}>
                        {/* 分享區預設收起:大部分時間只需要看支票卡。 */}
                        <Box
                          component="button"
                          type="button"
                          aria-expanded={shareOpen}
                          data-testid="cheque-share-toggle"
                          onClick={() => setShareOpen(!shareOpen)}
                          sx={{
                            display: 'block',
                            width: '100%',
                            p: 0,
                            m: 0,
                            border: 'none',
                            bgcolor: 'transparent',
                            font: 'inherit',
                            textAlign: 'left',
                            cursor: 'pointer',
                            borderRadius: 1,
                            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 }
                          }}
                        >
                          {sectionHead(
                            <Share2 size={16} aria-hidden="true" />,
                            ui.share,
                            <Box
                              component="span"
                              sx={{
                                display: 'flex',
                                color: 'text.secondary',
                                transform: shareOpen ? 'rotate(180deg)' : 'none',
                                transition: 'transform 180ms ease',
                                '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
                              }}
                            >
                              <ChevronDown size={18} aria-hidden="true" />
                            </Box>
                          )}
                        </Box>
                        <Collapse in={shareOpen} timeout={180}>
                          <Box sx={{ pt: 1.5 }}>
                            {/* 分享列仍是舊元件（複製欄、WhatsApp、WeChat…），所以把舊主題放回去。 */}
                            <LegacyThemeScope>
                              <ShareButtonGroup
                                isCTA
                                title={shareMessage()}
                                whatsapp={true}
                                line={true}
                                email={true}
                                wechat={true}
                                // ShareButtons shows WeChat only with a title; there is no link, so
                                // utils/wechat/share sends the message itself as WeChat text.
                                wechatTitle={t('Cheque Wizard')}
                                copy={true}
                                url=""
                                showCopy={true}
                                CopyTextFieldProps={{ multiline: true }}
                                hideShareButtons={hideShareButtons}
                              />
                            </LegacyThemeScope>
                          </Box>
                        </Collapse>
                      </Paper>

                      <Typography
                        variant="caption"
                        component="p"
                        sx={{ m: 0, color: 'text.secondary', textAlign: 'center' }}
                      >
                        {t('(For reference only)')}
                      </Typography>
                    </Stack>
                  </Box>

                  <Surface
                    open={insurerModalOpen}
                    onClose={handleInsurerModalClose}
                    // BottomSheetOrDialog paints its title in the accent; a title is not
                    // something to press, so this one brings its own neutral heading.
                    header={
                      <Typography variant="h6" component="span" sx={{ color: 'text.primary' }}>
                        {ui.pickPayee}
                      </Typography>
                    }
                    DialogProps={{ fullWidth: true, maxWidth: 'sm' }}
                    DialogParams={{ dialogTitleCloseButton: true }}
                    content={
                      <Stack sx={{ gap: 1.5 }}>
                        <OutlinedInput
                          value={payeeQuery}
                          onChange={event => setPayeeQuery(event.target.value)}
                          placeholder={ui.searchPayee}
                          startAdornment={
                            <InputAdornment position="start">
                              <Search size={16} aria-hidden="true" />
                            </InputAdornment>
                          }
                          inputProps={{ 'aria-label': ui.searchPayee, 'data-testid': 'cheque-payee-search' }}
                        />
                        {showRecentPayees && (
                          <Stack sx={{ gap: 1 }}>
                            <Typography
                              variant="overline"
                              component="h3"
                              sx={{ m: 0, display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}
                            >
                              <History size={14} aria-hidden="true" />
                              {ui.recent}
                            </Typography>
                            <Box data-testid="cheque-recent-payees" sx={PAYEE_GRID}>
                              {recentPayeeOptions.map(renderPayeeOption)}
                            </Box>
                            <Typography
                              variant="overline"
                              component="h3"
                              sx={{ m: 0, pt: 0.5, color: 'text.secondary' }}
                            >
                              {ui.allPayees}
                            </Typography>
                          </Stack>
                        )}
                        <Box sx={PAYEE_GRID}>{shownPayeeOptions.map(renderPayeeOption)}</Box>
                        {shownPayeeOptions.length === 0 && (
                          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                            {ui.noMatch}
                          </Typography>
                        )}
                      </Stack>
                    }
                  />

                  {/* 給客戶看:白底全螢幕,收款人和大寫用最大的一級字,顧問把手機轉過去就可以照抄。 */}
                  <Dialog
                    fullScreen
                    open={presenting}
                    onClose={() => setPresenting(false)}
                    aria-labelledby="cheque-present-title"
                  >
                    <Stack
                      data-testid="cheque-present-view"
                      sx={{ position: 'relative', minHeight: '100%', bgcolor: 'background.paper', px: 3, py: 6 }}
                    >
                      <IconButton
                        aria-label={ui.closePresent}
                        data-testid="cheque-present-close"
                        onClick={() => setPresenting(false)}
                        sx={{
                          position: 'absolute',
                          top: 'calc(var(--ion-safe-area-top, 0px) + 12px)',
                          right: 12,
                          color: 'text.secondary'
                        }}
                      >
                        <X size={22} aria-hidden="true" />
                      </IconButton>
                      {view === 'cheque' ? (
                        // 支票版:整張支票放大,客戶照住逐格抄。
                        <Stack sx={{ flex: 1, justifyContent: 'center', width: '100%', maxWidth: 1100, mx: 'auto' }}>
                          <Typography
                            id="cheque-present-title"
                            variant="overline"
                            sx={{ color: 'text.secondary', mb: 1 }}
                          >
                            {ui.viewCheque}
                          </Typography>
                          <ChequeFace
                            locale={intl.locale}
                            payeeHtml={payeeName}
                            amountText={amountText}
                            words={cashValueOut}
                            large
                          />
                        </Stack>
                      ) : (
                        <Stack
                          sx={{ flex: 1, justifyContent: 'center', gap: 4, maxWidth: 720, width: '100%', mx: 'auto' }}
                        >
                          <Stack sx={{ gap: 1, alignItems: 'center' }}>
                            <Typography id="cheque-present-title" variant="overline" sx={{ color: 'text.secondary' }}>
                              {t('Payee')}
                            </Typography>
                            <Typography
                              variant="display4"
                              component="p"
                              sx={{ m: 0, textAlign: 'center', wordBreak: 'break-word' }}
                              dangerouslySetInnerHTML={{ __html: sanitize(payeeName) }}
                            />
                          </Stack>
                          <Divider sx={{ borderStyle: 'dashed' }} />
                          <Stack sx={{ gap: 1, alignItems: 'center' }}>
                            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                              {t('Amount')}
                            </Typography>
                            <Typography variant="h4" component="p" sx={{ m: 0 }}>
                              {cashValue ? `$ ${Number(cashValue).toLocaleString('en-US')}` : '—'}
                            </Typography>
                            {cashValueOut && (
                              <Typography
                                variant="display4"
                                component="p"
                                sx={{ m: 0, textAlign: 'center', wordBreak: 'break-word' }}
                              >
                                {cashValueOut}
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      )}
                    </Stack>
                  </Dialog>
                </SoftDepthScope>
              </AccessCodeGate>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChequeOutView;
